import { toDetailedPrice } from '@cvo/domain';

/**
 * Composable for handling subscription fees display logic
 * @param subscriptionFees - Array of fee responses from plan or billing
 */
export function useFees(subscriptionFees: ComputedRef<AnySubscriptionFee[] | undefined>) {
  const i18n = useI18n();
  const { formatPrice } = usePriceFormatter();
  const userStore = useUserStore();

  const feesMap = computed<Map<AnySubscriptionFee['type'], AnySubscriptionFee>>(() => {
    const map = new Map<AnySubscriptionFee['type'], AnySubscriptionFee>();
    subscriptionFees.value?.forEach(fee => map.set(fee.type, fee));

    return map;
  });

  const hasSubsFees = computed<boolean>(() => feesMap.value.size > 0);

  function resolveFeePrice(fee: AnySubscriptionFee | undefined): ResolvedFeePrice | undefined {
    if (!fee) return undefined;
    if (isBillingFee(fee)) {
      return { gross: fee.gross.amount, net: fee.net.amount, currency: fee.gross.currency };
    }

    // Plans catalog fee: same multi-currency shape as a plan price. An unpriceable fee renders nothing.
    return toDetailedPrice(fee, userStore.currency);
  }

  const feeValues = computed<FeeValues | undefined>(() => {
    if (hasSubsFees.value) {
      const signUpFee = feesMap.value.get('SIGN_UP');
      const renewalFee = feesMap.value.get('RENEWAL');

      return {
        signUpFee,
        renewalFee,
        signUpPrice: resolveFeePrice(signUpFee),
        renewalPrice: resolveFeePrice(renewalFee),
      };
    }
  });

  const formattedFeeValues = computed<FormattedFeeValues>(() => {
    if (feeValues.value) {
      const { signUpPrice, renewalPrice } = feeValues.value;

      return {
        signUpPrice: signUpPrice ? formatPrice({ amount: signUpPrice.gross, currency: signUpPrice.currency }) : '',
        renewalPrice: renewalPrice ? formatPrice({ amount: renewalPrice.gross, currency: renewalPrice.currency }) : '',
      };
    }

    return { signUpPrice: '', renewalPrice: '' };
  });

  const hasSamePrice = computed<boolean>(() => feeValues.value?.signUpPrice?.gross === feeValues.value?.renewalPrice?.gross);

  const frequency = computed<string>(() => {
    const period: FeePeriodWithOneTime = feeValues.value?.renewalFee?.period ?? { unit: 'ONCE', amount: 0 };

    return i18n.t(`ui.plans.fee.frequency.${period.unit}`, period.amount);
  });

  function getBenefitText(signUpFee?: AnySubscriptionFee, renewalFee?: AnySubscriptionFee): string  {
    const { signUpPrice, renewalPrice } = formattedFeeValues.value;
     // Only signup OR same price
    if (!!signUpFee && (!renewalFee || hasSamePrice.value)) {
      return i18n.t('ui.plans.fee.benefit.programFee', { price: signUpPrice });
    }

    // Only renewal
    if (!!renewalFee && !signUpFee) {
      return i18n.t('ui.plans.fee.benefit.renewalProgramFee', {
        price: renewalPrice,
        frequency: frequency.value,
      });
    }

    // Both (different prices)
    return i18n.t('ui.plans.fee.benefit.entryAndProgramFee', {
      signUpPrice,
      frequency: frequency.value,
      renewalPrice,
    });
  }

  const feeBenefitText = computed<string | undefined>(() => {
    if (feeValues.value && formattedFeeValues.value) {
      return getBenefitText(feeValues.value.signUpFee, feeValues.value.renewalFee);
    }
  });

  const feePrices = computed<FeePrices>(() => {
    return {
      signUp: {
        gross: formattedFeeValues.value.signUpPrice,
        net: parseFloat(feeValues.value?.signUpPrice?.net ?? '0'),
      },
      renewal: {
        gross: formattedFeeValues.value.renewalPrice,
      },
    };
  });

  return {
    feeBenefitText,

    feesMap,
    hasSubsFees,
    frequency,
    feePrices,
  };
}
